import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { errorToast, successToast } from 'helpers'
import { ChatBotAPIService } from 'service/chat-bot-rest-api.service'
import { T } from '@tolgee/react'
import aiController from 'helpers/AIController'
import { CHATBOT_AVAILABILITY_OPTIONS, CHATBOT_VISIBILITY_OPTIONS } from 'helpers/chatbot'

export const chatBotInitialState = {
  inputValues: {
    name: '',
    storyId: null,
    selectedChatBot: null,
    tone: null,
    language: 'auto',
    intro: '',
    avatar: '',
    responseLength: '',
    monthlyLimit: '15000',
    isMonthlyLimit: true,
    isGuruEnabled: false,
    wordsConsumed: 0,
    author: '',
    allowLanguageChange: false,
    allowModeChange: true,
    allowWebSearch: false,
    allowContext: false,
    allowDatabases: false,
    isStartWithAudio: false,
    isStartWithWebSearch: false,
    instructions: '',
    isPublic: false,
    relatedGroups: [],
    modelId: aiController.getDefaultTextModel().value,
    // enableCustomUrl: false,
    // customUrlName: null,
    customUrls: {
      forum: { enableCustomUrl: false, customUrlName: null },
      public: { enableCustomUrl: false, customUrlName: null },
    },
    forum: {
      displayName: '',
      shortDescription: '',
      longDescription: '',
      category: 'eleo-chatbot-category-other',
    },
    visibilityOption: CHATBOT_VISIBILITY_OPTIONS[0].value,
    availabilityOption: CHATBOT_AVAILABILITY_OPTIONS[0].value,
    maxConversationLength: {
      forum: 10000,
      public: 10000,
      embed: 10000,
    },
  },
  settings: {
    activeOnEleo: false,
    websites: [],
    telegramToken: '',
    telegramBotActive: false,
    activeOnRemoteWebsite: false,
    chatPlacement: 'bottom-right',
    windowWidth: '700',
    windowHeight: '500',
    chatIcon: '',
    publishing: {
      forum: false,
      public: false,
      embed: false,
      telegram: false,
    },
  },
  chatBotData: [],
  chatBotOptionData: [],
  states: {
    generateChat: {
      loading: false,
      error: null,
    },
    getChatBotList: {
      loading: false,
      error: null,
    },
  },
}

export const generateChatBot = createAsyncThunk('chatBot/generate', async (data, { dispatch }) => {
  try {
    const response = await ChatBotAPIService.generateChatBot(data)
    successToast(
      <T
        keyName={`eleo-chatbot-published-success`}
        defaultValue='Chatbot published successfully<a1>{forumLink}</a1><a2>{publicLink}</a2>'
        params={{
          a1: (children) => (
            <div>
              <a
                className='font-light underline'
                href={`/chatbot/chat/${response.data.chatbot?.customUrls?.forum?.enableCustomUrl ? response.data.chatbot?.customUrls?.forum?.customUrlName : response.data.chatbot?.id}`}
              >
                {children}
              </a>
            </div>
          ),
          a2: (children) => (
            <div>
              <a
                className='font-light underline'
                href={`/chat/${response.data.chatbot?.customUrls?.public?.enableCustomUrl ? response.data.chatbot?.customUrls?.public?.customUrlName : response.data.chatbot?.id}`}
              >
                {children}
              </a>
            </div>
          ),
          forumLink: response.data.chatbot?.settings?.publishing?.forum ? 'forum link' : '',
          publicLink: response.data.chatbot?.settings?.publishing?.public ? 'public link' : '',
        }}
      />
    )

    dispatch(getChatBotList())

    if (response.id) {
      dispatch(setChatBotInputValues({ field: 'selectedChatBot', value: response.data.id }))
    }

    return response.data.chatbot
  } catch (error) {
    console.log(error)
    errorToast(
      <T keyName={error.response?.data?.error ?? error.message}>
        {error.response?.data?.error ?? error.message}
      </T>
    )
  }
})
export const generateChatBotDraft = createAsyncThunk(
  'chatBot/draft/generate',
  async (data, { dispatch }) => {
    try {
      const response = await ChatBotAPIService.generateChatBotDraft(data)
      const responseJSON = await response.json()
      if (responseJSON.success) {
        dispatch(getChatBotList())

        dispatch(
          setChatBotInputValues({
            field: 'selectedChatBot',
            value: responseJSON.originalChatBotId || responseJSON.id,
          })
        )

        return responseJSON.draft
      } else {
        errorToast(<T keyName={`eleo-error-${responseJSON.error}`}>{responseJSON.error}</T>)
      }
    } catch (error) {
      errorToast(error.message)
    }
  }
)

export const getChatBotList = createAsyncThunk('chatBot/getList', async () => {
  const response = await ChatBotAPIService.getChatBotList()
  const data = await response.json()
  return data
})

export const chatBotSlice = createSlice({
  name: 'chatBot',
  initialState: chatBotInitialState,
  reducers: {
    setChatBotInputValues: (state, action) => {
      const { field, value } = action.payload
      state.inputValues[field] = value
    },
    setChatBotSettings: (state, action) => {
      const { field, value } = action.payload
      state.settings[field] = value
    },
    setSelectedChatBotValues: (state, action) => {
      state.inputValues = action.payload
    },
    setSelectedChatBotSettings: (state, action) => {
      state.settings = action.payload
    },
    setClearFields: (state) => {
      state.inputValues = chatBotInitialState.inputValues
      state.settings = chatBotInitialState.settings
    },
    setChatBotForumData: (state, action) => {
      const { field, value } = action.payload
      state.inputValues.forum[field] = value
    },
    setChatBotCustomUrlData: (state, action) => {
      const { type, field, value } = action.payload
      state.inputValues.customUrls[type][field] = value
    },
    setChatBotHistoryLength: (state, action) => {
      const { type, value } = action.payload
      state.inputValues.maxConversationLength[type] = value
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateChatBot.pending, (state) => {
        state.states.generateChat.loading = true
        state.states.generateChat.error = null
      })
      .addCase(generateChatBot.fulfilled, (state, action) => {
        state.states.generateChat.loading = false
        state.states.generateChat.error = null
      })
      .addCase(generateChatBot.rejected, (state, action) => {
        state.states.generateChat.loading = false
        state.states.generateChat.error = 'Failed to create bot'
      })
      .addCase(getChatBotList.pending, (state) => {
        state.states.getChatBotList.loading = true
        state.states.getChatBotList.error = null
      })
      .addCase(getChatBotList.fulfilled, (state, action) => {
        state.states.getChatBotList.loading = false
        state.states.getChatBotList.error = null

        state.chatBotOptionData = action.payload.map((bot) => {
          return {
            value: bot.id,
            label: bot.name,
          }
        })

        state.chatBotData = action.payload
      })
      .addCase(getChatBotList.rejected, (state, action) => {
        state.states.getChatBotList.loading = false
        state.states.getChatBotList.error = 'Failed fetch chat bots'
      })
  },
})

// Action creators are generated for each case reducer function
export const {
  setChatBotInputValues,
  setSelectedChatBotValues,
  setClearFields,
  setChatBotSettings,
  setSelectedChatBotSettings,
  setChatBotForumData,
  setChatBotCustomUrlData,
  setChatBotHistoryLength,
} = chatBotSlice.actions

//selectors
export const SelectChatBotInputValues = (state) => state.chatBot.inputValues
export const SelectGenerateChatAsyncStates = (state) => state.chatBot.states.generateChat
export const SelectLoadChatsAsyncStates = (state) => state.chatBot.states.getChatBotList
export const SelectChatBotListOptions = (state) => state.chatBot.chatBotOptionData
export const SelectChatBotListData = (state) => state.chatBot.chatBotData
export const SelectChatBotSettings = (state) => state.chatBot.settings
export const SelectChatBotForumData = (state) => state.chatBot.inputValues.forum

export default chatBotSlice.reducer
